import React, { useState, useRef, Fragment, createElement } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { MaternityFile } from '../../types';
import { Search, FileText, Printer, Download, Baby, X, Trash2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 20;
  const MARGIN_BOTTOM_MM = 20;
  const MARGIN_LEFT_MM = 18;
  const MARGIN_RIGHT_MM = 18;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
export const MaternityFiles: React.FC = () => {
  const {
    maternityFiles,
    deleteMaternityFile,
    currentUser
  } = useData();
  const isAdmin = currentUser?.role === 'Administrator';
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState<MaternityFile | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const printableRef = useRef<HTMLDivElement>(null);
  const handleDeleteFile = (file: MaternityFile) => {
    if (window.confirm(`WARNING: This will permanently delete the maternity file for ${file.patient.firstName} ${file.patient.surname} (${file.bmcFileNumber}) and ALL of its records. This cannot be undone. Are you sure?`)) {
      deleteMaternityFile(file.id);
      setSelectedFile(null);
    }
  };
  const filteredFiles = maternityFiles.filter(m => {
    const q = searchQuery.toLowerCase();
    return m.patient.firstName.toLowerCase().includes(q) || m.patient.surname.toLowerCase().includes(q) || m.bmcFileNumber.toLowerCase().includes(q);
  }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const handlePrint = () => {
    document.body.classList.add('printing-maternity-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-maternity-modal');
    }, 500);
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current || !selectedFile) return;
    setIsGeneratingPdf(true);
    try {
      const filename = `BMED_Maternity_${selectedFile.bmcFileNumber}_${selectedFile.patient.firstName}_${selectedFile.patient.surname}.pdf`;
      await captureToA4Pdf(printableRef.current, filename);
    } catch (err) {
      console.error('PDF generation failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  return <div className="space-y-6" data-id="element-3217" data-mp="25vvo1bq4in5e">
      <div className="flex justify-between items-center print:hidden" data-id="element-3218" data-mp="20zoqo7bsmjz2">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-3219" data-mp="20xc0x7zsn5bg">
          Maternity Patient Files
        </h1>
      </div>

      {!selectedFile ? <Card noPadding data-id="element-3220" data-mp="29ysbbfqlz7fo">
          <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-3221" data-mp="29ya45o2er8sz">
            <div className="relative max-w-md" data-id="element-3222" data-mp="2ony68rieybhr">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3223" data-mp="2oqu3seqcmy5o">
                <Search className="h-5 w-5 text-gray-400" data-id="element-3224" data-mp="2js0rgpmstd73" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-pink-500 focus:border-pink-500 sm:text-sm" placeholder="Search by name or BMC file number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3225" data-mp="2xommztz6ypyc" />
            </div>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-3226" data-mp="t9kfyxhiaslu">
            {filteredFiles.length > 0 ? filteredFiles.map(file => <li key={file.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-3227" data-mp="kemidd3eyro6">
                  <div className="flex items-center" data-id="element-3228" data-mp="5jzvga5pfnag">
                    <div className="bg-pink-100 p-2 rounded-full mr-4" data-id="element-3229" data-mp="ah8d89mxxpb6">
                      <Baby className="h-6 w-6 text-pink-600" data-id="element-3230" data-mp="3smuv6wnp9zir" />
                    </div>
                    <div data-id="element-3231" data-mp="1gaarvs5hpup">
                      <p className="text-sm font-medium text-blue-600" data-id="element-3232" data-mp="1fid8fezti1s">
                        {file.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-3233" data-mp="1qvzw9vy75k7t">
                        {file.patient.firstName} {file.patient.surname}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-3234" data-mp="1tgpixmkac33g">
                        Registered:{' '}
                        {new Date(file.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4" data-id="element-3235" data-mp="1kgt2g881uajh">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${file.stage === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-3236" data-mp="1fh6m3pwxce6g">
                      {file.stage === 'COMPLETED' ? '✓ Completed' : 'Active (Nursing)'}
                    </span>
                    <Button onClick={() => setSelectedFile(file)} data-id="element-3237" data-mp="1ydps46cfllys">
                      <FileText className="w-4 h-4 mr-2" data-id="element-3238" data-mp="2vtv7c60sdbnt" /> View File
                    </Button>
                    {isAdmin && <Button variant="danger" onClick={() => handleDeleteFile(file)} data-id="element-3239" data-mp="2r2dfc7gqqlez">
                        <Trash2 className="w-4 h-4 mr-2" data-id="element-3240" data-mp="33do0zmmf0au2" /> Delete
                      </Button>}
                  </div>
                </li>) : <li className="p-12 text-center text-gray-500" data-id="element-3241" data-mp="3dbtzdu90iyu6">
                <Baby className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-3242" data-mp="3ddesrgjlcbqg" />
                <p className="text-lg font-medium text-gray-900" data-id="element-3243" data-mp="3he7187kn2clo">
                  No maternity files found
                </p>
              </li>}
          </ul>
        </Card> : <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-3244" data-mp="3r49v09zl3ul2">
          <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible maternity-modal-print" data-id="element-3245" data-mp="3w1js8bt7tajg">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-3246" data-mp="8v9aoeq5vb61">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-3247" data-mp="q7qlzq9eop3t">
                Maternity File — {selectedFile.bmcFileNumber}
              </h3>
              <div className="flex items-center space-x-2" data-id="element-3248" data-mp="144nlsh7i5gwu">
                <Button variant="outline" size="sm" onClick={handlePrint} data-id="element-3249" data-mp="z8ouakqvnis7">
                  <Printer className="w-4 h-4 mr-1" data-id="element-3250" data-mp="bhjfm8woawjh" /> Print
                </Button>
                <Button variant="primary" size="sm" onClick={handleDownloadPdf} disabled={isGeneratingPdf} data-id="element-3251" data-mp="6izntrveeqj7">
                  <Download className="w-4 h-4 mr-1" data-id="element-3252" data-mp="gh5o651k3oly" />{' '}
                  {isGeneratingPdf ? 'Generating...' : 'Download PDF'}
                </Button>
                {isAdmin && <Button variant="danger" size="sm" onClick={() => handleDeleteFile(selectedFile)} data-id="element-3253" data-mp="3oumh9r7d1xik">
                    <Trash2 className="w-4 h-4 mr-1" data-id="element-3254" data-mp="1zeiqd14wz5eh" /> Delete
                  </Button>}
                <button onClick={() => setSelectedFile(null)} className="p-1.5 hover:bg-gray-100 rounded" data-id="element-3255" data-mp="248n3610v27nh">
                  <X className="w-5 h-5 text-gray-500" data-id="element-3256" data-mp="1uhsdsnsbirxt" />
                </button>
              </div>
            </div>

            <div ref={printableRef} className="p-8 bg-white text-sm font-sans text-black" data-id="element-3257" data-mp="2i6usbmrno4o3">
              {/* Header */}
              <div className="flex justify-between items-start mb-8 border-b-2 border-black pb-4" data-id="element-3258" data-mp="2966ht21h0mfy">
                <div className="flex items-center" data-id="element-3259" data-mp="2e485y1dr7bya">
                  <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-3260" data-mp="2e6ugm9vxy7kq" />
                  <div data-id="element-3261" data-mp="a4vzbll9huy4">
                    <h1 className="text-xl font-bold uppercase" data-id="element-3262" data-mp="a6p6d1zseh6n">
                      BMED MEDICAL CENTRE
                    </h1>
                    <p className="text-xs italic" data-id="element-3263" data-mp="e93o1irpzawc">Caring Healing Leading</p>
                  </div>
                </div>
                <div className="text-right" data-id="element-3264" data-mp="5epaplqr2afv">
                  <h2 className="text-lg font-bold uppercase tracking-widest border-b border-black pb-1 mb-2" data-id="element-3265" data-mp="5de99sks1erx">
                    Maternity Record
                  </h2>
                  <p data-id="element-3266" data-mp="3rmf5p1tttxau">
                    <span className="font-semibold" data-id="element-3267" data-mp="3rmyloczx2bm8">File No:</span>{' '}
                    {selectedFile.bmcFileNumber}
                  </p>
                  <p data-id="element-3268" data-mp="3doh4yz8fhzdt">
                    <span className="font-semibold" data-id="element-3269" data-mp="3ikxqrms5o1nq">Registered:</span>{' '}
                    {new Date(selectedFile.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* PATIENT'S DETAILS */}
              <div className="mb-6" data-id="element-3270" data-mp="1gtceflcu3gnv">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3271" data-mp="1qxsry82ucloi">
                  PATIENT'S DETAILS
                </h3>
                <div className="grid grid-cols-2 gap-y-2 gap-x-8" data-id="element-3272" data-mp="1lzhgjswwe346">
                  <div data-id="element-3273" data-mp="1zq30lky38j3n">
                    <span className="font-semibold" data-id="element-3274" data-mp="1zqmhcyc051g3">First Name:</span>{' '}
                    {selectedFile.patient.firstName}
                  </div>
                  <div data-id="element-3275" data-mp="1ux9wmzag1esm">
                    <span className="font-semibold" data-id="element-3276" data-mp="29oaf7x4wmp2h">Surname:</span>{' '}
                    {selectedFile.patient.surname}
                  </div>
                  <div data-id="element-3277" data-mp="24oea1ukh0rlc">
                    <span className="font-semibold" data-id="element-3278" data-mp="24oxqt7ydx9xs">Other Names:</span>{' '}
                    {selectedFile.patient.otherNames}
                  </div>
                  <div data-id="element-3279" data-mp="2en3tvsvxgm48">
                    <span className="font-semibold" data-id="element-3280" data-mp="2eoomhcyoluzg">Age:</span>{' '}
                    {selectedFile.patient.age}
                  </div>
                  <div data-id="element-3281" data-mp="ocr2lqp0eupe">
                    <span className="font-semibold" data-id="element-3282" data-mp="k9ug3owcmeg6">Religion:</span>{' '}
                    {selectedFile.patient.religion}
                  </div>
                  <div data-id="element-3283" data-mp="2jfxaa1ncn542">
                    <span className="font-semibold" data-id="element-3284" data-mp="2jggphalm7fee">Church Attended:</span>{' '}
                    {selectedFile.patient.churchAttended}
                  </div>
                  <div className="col-span-2" data-id="element-3285" data-mp="2tap7h4d0rpyb">
                    <span className="font-semibold" data-id="element-3286" data-mp="2yb3h2qu9r7yg">Residential Address:</span>{' '}
                    {selectedFile.patient.residentialAddress}
                  </div>
                  <div className="col-span-2" data-id="element-3287" data-mp="32avt5divfw8k">
                    <span className="font-semibold" data-id="element-3288" data-mp="37bjky1exch3u">Location/Landmark:</span>{' '}
                    {selectedFile.patient.locationLandmark}
                  </div>
                </div>
              </div>

              {/* SPOUSE DETAILS */}
              <div className="mb-6" data-id="element-3289" data-mp="3h131h0f5zyvj">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3290" data-mp="3m17jiuvq11nv">
                  SPOUSE DETAILS (OPTIONAL)
                </h3>
                <div className="grid grid-cols-2 gap-y-2 gap-x-8" data-id="element-3291" data-mp="3q3kohh2n8vjl">
                  <div data-id="element-3292" data-mp="1eexrik835zsn">
                    <span className="font-semibold" data-id="element-3293" data-mp="1eg8skca5ctg2">First Name:</span>{' '}
                    {selectedFile.spouse.firstName}
                  </div>
                  <div data-id="element-3294" data-mp="1afyox69tuf4d">
                    <span className="font-semibold" data-id="element-3295" data-mp="15e9dvqmlu1ga">Surname:</span>{' '}
                    {selectedFile.spouse.surname}
                  </div>
                  <div data-id="element-3296" data-mp="10gzebi5i49eq">
                    <span className="font-semibold" data-id="element-3297" data-mp="10hr9irv6s54h">Other Names:</span>{' '}
                    {selectedFile.spouse.otherNames}
                  </div>
                  <div data-id="element-3298" data-mp="1t54xw1ktjscz">
                    <span className="font-semibold" data-id="element-3299" data-mp="1t4loxad5ceb3">Occupation:</span>{' '}
                    {selectedFile.spouse.occupation}
                  </div>
                  <div className="col-span-2" data-id="element-3300" data-mp="1jesgujc7qhbl">
                    <span className="font-semibold" data-id="element-3301" data-mp="1je0jp42ywbj7">Contact:</span>{' '}
                    {selectedFile.spouse.contact}
                  </div>
                </div>
              </div>

              {/* ADMISSION */}
              <div className="mb-6" data-id="element-3302" data-mp="2c0v5c4bitgy9">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3303" data-mp="1wketyd6mns6k">
                  ADMISSION
                </h3>
                <div className="flex space-x-16" data-id="element-3304" data-mp="20nkxve69p79v">
                  <div data-id="element-3305" data-mp="20k6zsi4mzk88">
                    <span className="font-semibold" data-id="element-3306" data-mp="1mna6ma11pqo6">Date:</span>{' '}
                    {selectedFile.admission.date}
                  </div>
                  <div data-id="element-3307" data-mp="1rmdc1vkxtywo">
                    <span className="font-semibold" data-id="element-3308" data-mp="1rnohs0xy1gqf">Time of Admission:</span>{' '}
                    {selectedFile.admission.time}
                  </div>
                </div>
              </div>

              {/* GENERAL EXAMINATION */}
              <div className="mb-6" data-id="element-3309" data-mp="25meg1jwi1mgd">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3310" data-mp="25lcqkaeqkxf4">
                  GENERAL EXAMINATION
                </h3>
                <div className="space-y-2" data-id="element-3311" data-mp="2aid0wximcmc1">
                  <div data-id="element-3312" data-mp="2y98w84ma9zo1">
                    <span className="font-semibold" data-id="element-3313" data-mp="2y8z5getezoha">
                      Cardiovascular System:
                    </span>{' '}
                    {selectedFile.generalExam.cardiovascularSystem}
                  </div>
                  <div className="flex space-x-8" data-id="element-3314" data-mp="rjorfrmlxfnn">
                    <div data-id="element-3315" data-mp="mlvpjasc77t6">
                      <span className="font-semibold" data-id="element-3316" data-mp="mmomxl5eokyn">Temp:</span>{' '}
                      {selectedFile.generalExam.temp}
                    </div>
                    <div data-id="element-3317" data-mp="wc86yu4uukv3">
                      <span className="font-semibold" data-id="element-3318" data-mp="we1e0ajdr73m">Pulse:</span>{' '}
                      {selectedFile.generalExam.pulse}
                    </div>
                    <div data-id="element-3319" data-mp="3o2lu69w2tuq">
                      <span className="font-semibold" data-id="element-3320" data-mp="3ouhti7eetlj">Respiration:</span>{' '}
                      {selectedFile.generalExam.respiration}
                    </div>
                    <div data-id="element-3321" data-mp="hlhgpqiwvtu3">
                      <span className="font-semibold" data-id="element-3322" data-mp="co9568s9qo1t">BP:</span>{' '}
                      {selectedFile.generalExam.bp}
                    </div>
                  </div>
                  <div className="flex space-x-16" data-id="element-3323" data-mp="1t4twtfxp7d93">
                    <div data-id="element-3324" data-mp="1o34hvp7cuffq">
                      <span className="font-semibold" data-id="element-3325" data-mp="1o3nxv0dg2tr4">Oedema:</span>{' '}
                      {selectedFile.generalExam.oedema}
                    </div>
                    <div data-id="element-3326" data-mp="qotaonjaqhbs">
                      <span className="font-semibold" data-id="element-3327" data-mp="qo1epblsehkz">Anemia:</span>{' '}
                      {selectedFile.generalExam.anemia}
                    </div>
                  </div>
                  <div data-id="element-3328" data-mp="zp7zhwvgyek1">
                    <span className="font-semibold" data-id="element-3329" data-mp="zpr8go355slx">Respiratory System:</span>{' '}
                    {selectedFile.generalExam.respiratorySystem}
                  </div>
                  <div data-id="element-3330" data-mp="70tx74xckw2o">
                    <span className="font-semibold" data-id="element-3331" data-mp="7252582j49vd">
                      Central Nervous System:
                    </span>{' '}
                    {selectedFile.generalExam.centralNervousSystem}
                  </div>
                  <div className="flex space-x-8" data-id="element-3332" data-mp="gthwc6bsvc4k">
                    <div data-id="element-3333" data-mp="3fgdpo1q72ago">
                      <span className="font-semibold" data-id="element-3334" data-mp="3fgvrrezjfiwi">Breasts:</span>{' '}
                      {selectedFile.generalExam.breasts}
                    </div>
                    <div data-id="element-3335" data-mp="3cxinxpfdnjp4">
                      <span className="font-semibold" data-id="element-3336" data-mp="3cvo4kfjr7zn5">Varicosities:</span>{' '}
                      {selectedFile.generalExam.varicosities}
                    </div>
                    <div data-id="element-3337" data-mp="32y9zfc3a6xa5">
                      <span className="font-semibold" data-id="element-3338" data-mp="32xrr3h3cgslv">Vaginal Discharge:</span>{' '}
                      {selectedFile.generalExam.vaginalDischarge}
                    </div>
                  </div>
                  <div data-id="element-3339" data-mp="2tz6cty4wnzqw">
                    <span className="font-semibold" data-id="element-3340" data-mp="2tzfu8xbvwykz">Other Abnormalities:</span>{' '}
                    {selectedFile.generalExam.otherAbnormalities}
                  </div>
                  <div className="flex space-x-8 mt-2" data-id="element-3341" data-mp="2k9d372obpupa">
                    <div data-id="element-3342" data-mp="2f98nvfz5j60n">
                      <span className="font-semibold" data-id="element-3343" data-mp="2fa06lq7nh99i">MS Urine: SG:</span>{' '}
                      {selectedFile.generalExam.msUrine.sg}
                    </div>
                    <div data-id="element-3344" data-mp="2b9gcywmacns4">
                      <span className="font-semibold" data-id="element-3345" data-mp="2680qd04gm97m">Albumen:</span>{' '}
                      {selectedFile.generalExam.msUrine.albumen}
                    </div>
                    <div data-id="element-3346" data-mp="3u3fax70pg548">
                      <span className="font-semibold" data-id="element-3347" data-mp="2yp2uw2d2f8n">Acetone:</span>{' '}
                      {selectedFile.generalExam.msUrine.acetone}
                    </div>
                    <div data-id="element-3348" data-mp="3l6mr84zlu60w">
                      <span className="font-semibold" data-id="element-3349" data-mp="3p5vkz9una3wg">Sugar:</span>{' '}
                      {selectedFile.generalExam.msUrine.sugar}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-3350" data-mp="3bcxkj93chlm1">
                    <div data-id="element-3351" data-mp="3g6ijb3crijma">
                      <span className="font-semibold" data-id="element-3352" data-mp="3g6s32977rujj">Hb%:</span>{' '}
                      {selectedFile.generalExam.hb}{' '}
                      <span className="italic text-xs" data-id="element-3353" data-mp="36fnohalgabp8">
                        (NOTE: Refer to UTH if HB% is 6grams or below)
                      </span>
                    </div>
                    <div data-id="element-3354" data-mp="2shzcks9d2ewb">
                      <span className="font-semibold" data-id="element-3355" data-mp="2xeps9eh978h7">ABO:</span>{' '}
                      {selectedFile.generalExam.abo}
                    </div>
                    <div data-id="element-3356" data-mp="2dtpkh6ccusup">
                      <span className="font-semibold" data-id="element-3357" data-mp="2dv0on79pq2mc">Rh:</span>{' '}
                      {selectedFile.generalExam.rh}
                    </div>
                  </div>
                </div>
              </div>

              {/* PRESENT PREGNANCY */}
              <div className="mb-6" data-id="element-3358" data-mp="2ir7ku8cb3zr3">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3359" data-mp="2iudabooul3nc">
                  PRESENT PREGNANCY
                </h3>
                <div className="space-y-2" data-id="element-3360" data-mp="2rsysvjubkio4">
                  <div className="flex space-x-8" data-id="element-3361" data-mp="2rs5t52ts2tfh">
                    <div data-id="element-3362" data-mp="36hv4trzhyfu0">
                      <span className="font-semibold" data-id="element-3363" data-mp="36i4okxty7qr9">LMP:</span>{' '}
                      {selectedFile.presentPregnancy.lmp}
                    </div>
                    <div data-id="element-3364" data-mp="31kuydfyqjb2d">
                      <span className="font-semibold" data-id="element-3365" data-mp="3fi9suz3ui917">Quickening:</span>{' '}
                      {selectedFile.presentPregnancy.quickening}
                    </div>
                    <div data-id="element-3366" data-mp="3ahdhk0h5zklr">
                      <span className="font-semibold" data-id="element-3367" data-mp="3n1wpjo49vhdf">EDD:</span>{' '}
                      {selectedFile.presentPregnancy.edd}
                    </div>
                    <div data-id="element-3368" data-mp="3j01o8jmcd3zn">
                      <span className="font-semibold" data-id="element-3369" data-mp="3j2xmk923puom">Gestation Age:</span>{' '}
                      {selectedFile.presentPregnancy.gestationAge}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-3370" data-mp="3rzgf3k6f7gko">
                    <div data-id="element-3371" data-mp="34cqe8m5t5s1n">
                      <span className="font-semibold" data-id="element-3372" data-mp="34byjteny60cy">
                        Summary of ANC Findings 1st BP:
                      </span>{' '}
                      {selectedFile.presentPregnancy.ancSummaryFirstBp}
                    </div>
                    <div data-id="element-3373" data-mp="39b1novs6y0jc">
                      <span className="font-semibold" data-id="element-3374" data-mp="399qlv1ib32uv">at:</span>{' '}
                      {selectedFile.presentPregnancy.ancSummaryAtWeeks} Weeks
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-3375" data-mp="2gmw0m2dnzzgc">
                    <div data-id="element-3376" data-mp="2gk9wkcpzg22v">
                      <span className="font-semibold" data-id="element-3377" data-mp="w3jxfft6ecks">
                        Most recent Syphilis Test Date:
                      </span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.date}
                    </div>
                    <div data-id="element-3378" data-mp="n4yidumwxfor">
                      <span className="font-semibold" data-id="element-3379" data-mp="n1keuj2ij0fq">Result:</span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.result}
                    </div>
                    <div data-id="element-3380" data-mp="1ev2xpevtfcj4">
                      <span className="font-semibold" data-id="element-3381" data-mp="1esqktg4wzscx">Treatment:</span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.treatment}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-3382" data-mp="15w7ri2sru2ft">
                    <div data-id="element-3383" data-mp="1arwy2q37kjlm">
                      <span className="font-semibold" data-id="element-3384" data-mp="1ar51bbxvkftr">
                        Most recent HP Test Date:
                      </span>{' '}
                      {selectedFile.presentPregnancy.hpTest.date}
                    </div>
                    <div data-id="element-3385" data-mp="212ywy3rkrldn">
                      <span className="font-semibold" data-id="element-3386" data-mp="2138fx7e7cs9u">Result:</span>{' '}
                      {selectedFile.presentPregnancy.hpTest.result}
                    </div>
                  </div>
                  <div data-id="element-3387" data-mp="1rcnhbjyj3nqx">
                    <span className="font-semibold" data-id="element-3388" data-mp="1rbukpbtaaemi">Pelvic Assessment:</span>{' '}
                    {selectedFile.presentPregnancy.pelvicAssessment}
                  </div>
                  <div data-id="element-3389" data-mp="1id0h31hjcnzg">
                    <span className="font-semibold" data-id="element-3390" data-mp="1ida7uraemz67">Abnormalities:</span>{' '}
                    {selectedFile.presentPregnancy.abnormalities}
                  </div>
                </div>
              </div>

              {/* OBSERVATIONS FOR LATENT PHASE LABOUR */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3391" data-mp="14czi5rktzqmm">
                <h3 className="font-bold uppercase underline mb-1" data-id="element-3392" data-mp="14f2fyxs86o1w">
                  OBSERVATIONS FOR LATENT PHASE LABOUR
                </h3>
                <p className="text-xs italic mb-2" data-id="element-3393" data-mp="2yrs93darrt88">
                  NOTE: Refer to UTH if latest phase exceeds 8 hours
                </p>
                <table className="w-full text-xs border-collapse border border-black text-center" data-id="element-3394" data-mp="kgvdz663d8lk">
                  <thead data-id="element-3395" data-mp="pfy7pugmfse8">
                    <tr className="bg-gray-100" data-id="element-3396" data-mp="u916ch099s1a">
                      <th className="border border-black p-1" data-id="element-3397" data-mp="u9avy3he1x6g">Date</th>
                      <th className="border border-black p-1" data-id="element-3398" data-mp="yamexq5rbk5o">Time</th>
                      <th className="border border-black p-1" data-id="element-3399" data-mp="1k3qslnfi9c8">Pulse</th>
                      <th className="border border-black p-1" data-id="element-3400" data-mp="1m6o7qqwv4qz">BP</th>
                      <th className="border border-black p-1" data-id="element-3401" data-mp="6jzsufckfqp5">FH</th>
                      <th className="border border-black p-1" data-id="element-3402" data-mp="akaa4o5t63jb">Temp</th>
                      <th className="border border-black p-1" data-id="element-3403" data-mp="an4ukfotnocq">Urinalysis</th>
                      <th className="border border-black p-1" data-id="element-3404" data-mp="fl6cuyefbjn5">Contractions</th>
                      <th className="border border-black p-1" data-id="element-3405" data-mp="1lz5m22d2lqw6">
                        Cervical Dilation
                      </th>
                      <th className="border border-black p-1" data-id="element-3406" data-mp="1r14ptdcq2ptn">
                        Membranes Liquor
                      </th>
                      <th className="border border-black p-1" data-id="element-3407" data-mp="1kj1ll1tjiyzz">Remarks</th>
                      <th className="border border-black p-1" data-id="element-3408" data-mp="1kl4kkbco82gu">Sign</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-3409" data-mp="1uh7mbg3m1z39">
                    {selectedFile.labourObservations.length > 0 ? selectedFile.labourObservations.map(obs => <tr key={obs.id} data-id="element-3410" data-mp="28ensqrlwpsv0">
                          <td className="border border-black p-1" data-id="element-3411" data-mp="23i5tu8d9coql">
                            {obs.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-3412" data-mp="2i60lrmjfyp2m">
                            {obs.time}
                          </td>
                          <td className="border border-black p-1" data-id="element-3413" data-mp="2r786f1t1bre8">
                            {obs.pulse}
                          </td>
                          <td className="border border-black p-1" data-id="element-3414" data-mp="2m96oik7chy4c">{obs.bp}</td>
                          <td className="border border-black p-1" data-id="element-3415" data-mp="35tfbmck6q0fc">{obs.fh}</td>
                          <td className="border border-black p-1" data-id="element-3416" data-mp="3at0ayo39oasl">
                            {obs.temp}
                          </td>
                          <td className="border border-black p-1" data-id="element-3417" data-mp="2wu0pfpb1wc0n">
                            {obs.urinalysis}
                          </td>
                          <td className="border border-black p-1" data-id="element-3418" data-mp="30wxb5ovw0o8t">
                            {obs.contractions}
                          </td>
                          <td className="border border-black p-1" data-id="element-3419" data-mp="2runxiq5boer7">
                            {obs.cervicalDilation}
                          </td>
                          <td className="border border-black p-1" data-id="element-3420" data-mp="2d72vkzk6olkv">
                            {obs.membranesLiquor}
                          </td>
                          <td className="border border-black p-1 text-left" data-id="element-3421" data-mp="2441uxee37mol">
                            {obs.remarks}
                          </td>
                          <td className="border border-black p-1" data-id="element-3422" data-mp="292vadccxpnre">
                            {obs.sign}
                          </td>
                        </tr>) : <tr data-id="element-3423" data-mp="9uwn6arvikya">
                        <td colSpan={12} className="border border-black p-4 text-gray-400 italic" data-id="element-3424" data-mp="4x4v9gr8ayu3">
                          No observations recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DESCRIPTION OF LABOUR AND DELIVERY */}
              <div className="mb-6" data-id="element-3425" data-mp="okaxpm76r8f9">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3426" data-mp="ongn72jq8cbi">
                  DESCRIPTION OF LABOUR AND DELIVERY
                </h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3427" data-mp="39c4on4fxyzjb">
                  <div data-id="element-3428" data-mp="34crq3mfpk40w">
                    <span className="font-semibold" data-id="element-3429" data-mp="34c8h4v81cpz0">Laceration:</span>{' '}
                    {selectedFile.delivery.laceration} |{' '}
                    <span className="font-semibold" data-id="element-3430" data-mp="1jwahvlbuzkd0">Anterior:</span>{' '}
                    {selectedFile.delivery.anterior} |{' '}
                    <span className="font-semibold" data-id="element-3431" data-mp="1svfeun7rv5n2">Posterior:</span>{' '}
                    {selectedFile.delivery.posterior} |{' '}
                    <span className="font-semibold" data-id="element-3432" data-mp="1xmdurloum1ps">None:</span>{' '}
                    {selectedFile.delivery.none}
                  </div>
                  <div data-id="element-3433" data-mp="22kyvdm80iner">
                    <span className="font-semibold" data-id="element-3434" data-mp="27kbim82spwhv">Repaired:</span>{' '}
                    {selectedFile.delivery.repaired}
                  </div>
                  <div data-id="element-3435" data-mp="2ckpslvnyjgij">
                    <span className="font-semibold" data-id="element-3436" data-mp="2chacqqmgm3g2">Membranes:</span>{' '}
                    {selectedFile.delivery.membranes}
                  </div>
                  <div data-id="element-3437" data-mp="i65e60q8ch7l">
                    <span className="font-semibold" data-id="element-3438" data-mp="i6otd9ohwrhx">Date:</span>{' '}
                    {selectedFile.delivery.membranesDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-3439" data-mp="m8saadtxooal">Time:</span>{' '}
                    {selectedFile.delivery.membranesTime}
                  </div>
                  <div data-id="element-3440" data-mp="3ufynvjsf2e65">
                    <span className="font-semibold" data-id="element-3441" data-mp="3ugrjpppu7j9i">Episiotomy:</span>{' '}
                    {selectedFile.delivery.episiotomy}
                  </div>
                  <div data-id="element-3442" data-mp="80x4o22prcg5">
                    <span className="font-semibold" data-id="element-3443" data-mp="82qaxg9ezunm">Repaired:</span>{' '}
                    {selectedFile.delivery.episiotomyRepaired}
                  </div>
                  <div data-id="element-3444" data-mp="3bif7tw7jjva8">
                    <span className="font-semibold" data-id="element-3445" data-mp="3bixapbopl7r4">Maternal ARV:</span>{' '}
                    {selectedFile.delivery.maternalARV}
                  </div>
                  <div data-id="element-3446" data-mp="3pfkdhv3ces4y">
                    <span className="font-semibold" data-id="element-3447" data-mp="3kic4ak069yfu">
                      Which ones (NPV, AZT, OTHER):
                    </span>{' '}
                    {selectedFile.delivery.arvTypes}
                  </div>
                  <div className="col-span-2 flex space-x-8" data-id="element-3448" data-mp="zte9ckjpelxl">
                    <div data-id="element-3449" data-mp="189n0mcc5rxti">
                      <span className="font-semibold" data-id="element-3450" data-mp="1893qviwnwfqk">Uterine massage:</span>{' '}
                      {selectedFile.delivery.uterineMassage}
                    </div>
                    <div data-id="element-3451" data-mp="1c8vwbmqre5rp">
                      <span className="font-semibold" data-id="element-3452" data-mp="ybz3xguzsg8p">
                        Mother Already on HAART:
                      </span>{' '}
                      {selectedFile.delivery.motherOnHAART}
                    </div>
                    <div data-id="element-3453" data-mp="1r165yptnnnyp">
                      <span className="font-semibold" data-id="element-3454" data-mp="1r0wknfjk24zc">Cytotec:</span>{' '}
                      {selectedFile.delivery.cytotec}
                    </div>
                    <div data-id="element-3455" data-mp="1hb3bsmass3ys">
                      <span className="font-semibold" data-id="element-3456" data-mp="1h9idqmp9y2w6">Syntometrine:</span>{' '}
                      {selectedFile.delivery.syntometrine}
                    </div>
                  </div>
                  <div data-id="element-3457" data-mp="29xxrz70l0h6g">
                    <span className="font-semibold" data-id="element-3458" data-mp="29xo1zjfjea0r">1st stage began Date:</span>{' '}
                    {selectedFile.delivery.firstStageBeganDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-3459" data-mp="3gq858rqk5pcn">Time:</span>{' '}
                    {selectedFile.delivery.firstStageBeganTime}
                  </div>
                  <div data-id="element-3460" data-mp="3lmq81m2bvdmc">
                    <span className="font-semibold" data-id="element-3461" data-mp="3qfrovzm3hn8k">Placenta Delivery:</span>{' '}
                    {selectedFile.delivery.placentaDelivery}
                  </div>
                  <div data-id="element-3462" data-mp="3vgp3k27wbz5b">
                    <span className="font-semibold" data-id="element-3463" data-mp="2xqu8k21g20ig">
                      Complete dilation Date:
                    </span>{' '}
                    {selectedFile.delivery.completeDilationDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-3464" data-mp="37p9xpzgwuzpb">Time:</span>{' '}
                    {selectedFile.delivery.completeDilationTime}
                  </div>
                  <div data-id="element-3465" data-mp="3bry7qahd2cnv">
                    <span className="font-semibold" data-id="element-3466" data-mp="3boagjhze22cb">
                      Placenta Delivered Date:
                    </span>{' '}
                    {selectedFile.delivery.placentaDeliveredDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-3467" data-mp="2bifa3dntdrz8">Time:</span>{' '}
                    {selectedFile.delivery.placentaDeliveredTime}
                  </div>
                  <div className="col-span-2" data-id="element-3468" data-mp="27hd9ase917v5">
                    <span className="font-semibold" data-id="element-3469" data-mp="22pv946k1vbb8">
                      Abnormality at Dilation:
                    </span>{' '}
                    {selectedFile.delivery.abnormalityAtDilation} |{' '}
                    <span className="font-semibold" data-id="element-3470" data-mp="1ssgso6y4mmix">Describe:</span>{' '}
                    {selectedFile.delivery.abnormalityAtDilationDesc}
                  </div>
                  <div data-id="element-3471" data-mp="1nuypyyapd3jd">
                    <span className="font-semibold" data-id="element-3472" data-mp="1jqzdqrwggqvt">
                      Postpartum hemorrhage:
                    </span>{' '}
                    {selectedFile.delivery.postpartumHemorrhage}
                  </div>
                  <div data-id="element-3473" data-mp="3e5jx9191igdm">
                    <span className="font-semibold" data-id="element-3474" data-mp="3e4ryxio264jn">Delivery Method:</span>{' '}
                    {selectedFile.delivery.deliveryMethod}
                  </div>
                  <div data-id="element-3475" data-mp="3saagczixg9sl">
                    <span className="font-semibold" data-id="element-3476" data-mp="3s9r5u3vlwnol">EBL:</span>{' '}
                    {selectedFile.delivery.ebl} cc
                  </div>
                  <div className="col-span-2" data-id="element-3477" data-mp="xzb8c51neji">
                    <span className="font-semibold" data-id="element-3478" data-mp="3jb6vpr443mbz">
                      Abnormality at Delivery:
                    </span>{' '}
                    {selectedFile.delivery.abnormalityAtDelivery} |{' '}
                    <span className="font-semibold" data-id="element-3479" data-mp="fugm729bmr5j">Describe:</span>{' '}
                    {selectedFile.delivery.abnormalityAtDeliveryDesc}
                  </div>
                  <div className="col-span-2" data-id="element-3480" data-mp="5vhkk3mz3vs1">
                    <span className="font-semibold" data-id="element-3481" data-mp="5upoystdly1r">Complications:</span>{' '}
                    {selectedFile.delivery.complications}
                  </div>
                </div>

                <div className="mt-4" data-id="element-3482" data-mp="2qkf1v0air9rt">
                  <h4 className="font-bold text-sm mb-1" data-id="element-3483" data-mp="2qnjfspdsdjvo">DELIVERY SUMMARY:</h4>
                  <table className="w-full text-xs border-collapse border border-black text-center" data-id="element-3484" data-mp="2vm4fmnp4m1jl">
                    <thead data-id="element-3485" data-mp="2nzh5trrio3pq">
                      <tr className="bg-gray-100" data-id="element-3486" data-mp="2o2v3wnt5dqrd">
                        <th className="border border-black p-1" data-id="element-3487" data-mp="2j15sh720jb2r">DATE</th>
                        <th className="border border-black p-1" data-id="element-3488" data-mp="2x28h9rke6zhd">
                          TIME OF BIRTH
                        </th>
                        <th className="border border-black p-1" data-id="element-3489" data-mp="2s35bu60i2r8v">SEX</th>
                        <th className="border border-black p-1" data-id="element-3490" data-mp="24c9phoetco90">WEIGHT</th>
                        <th className="border border-black p-1" data-id="element-3491" data-mp="20bprkimf1glt">
                          DELIVERED BY
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-3492" data-mp="2e4p8mp1ujivh">
                      {selectedFile.delivery.deliverySummary.length > 0 ? selectedFile.delivery.deliverySummary.map(sum => <tr key={sum.id} data-id="element-3493" data-mp="1lkgszk0jckwj">
                            <td className="border border-black p-1" data-id="element-3494" data-mp="1go8kuqkr9ryv">
                              {sum.date}
                            </td>
                            <td className="border border-black p-1" data-id="element-3495" data-mp="1uc0j2d9l1o1n">
                              {sum.timeOfBirth}
                            </td>
                            <td className="border border-black p-1" data-id="element-3496" data-mp="247tshns091f7">
                              {sum.sex}
                            </td>
                            <td className="border border-black p-1" data-id="element-3497" data-mp="290n1inuiavx3">
                              {sum.weight}
                            </td>
                            <td className="border border-black p-1" data-id="element-3498" data-mp="2hzi65vhwjxya">
                              {sum.deliveredBy}
                            </td>
                          </tr>) : <tr data-id="element-3499" data-mp="2rwwhxhsvryk9">
                          <td colSpan={5} className="border border-black p-4 text-gray-400 italic" data-id="element-3500" data-mp="2rx69vaxhkfsl">
                            No delivery summary recorded
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* NEW BORN EVALUATION */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3501" data-mp="3213awwgm3oml">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3502" data-mp="2x31r29bd3la2">
                  NEW BORN EVALUATION
                </h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3503" data-mp="3ts0y5l07mh0o">
                  <div data-id="element-3504" data-mp="3prqithqj2e95">
                    <span className="font-semibold" data-id="element-3505" data-mp="3pr78am37is55">EGA:</span>{' '}
                    {selectedFile.newbornEvaluation.ega}
                  </div>
                  <div data-id="element-3506" data-mp="3kugvc7mp8cny">
                    <span className="font-semibold" data-id="element-3507" data-mp="3ftjepzhc7qok">ALIVE:</span>{' '}
                    {selectedFile.newbornEvaluation.alive}
                  </div>
                  <div data-id="element-3508" data-mp="29es8dg9g3dn5">
                    <span className="font-semibold" data-id="element-3509" data-mp="29aunap3b4c0m">Neonatal Death:</span>{' '}
                    {selectedFile.newbornEvaluation.neonatalDeath}
                  </div>
                  <div data-id="element-3510" data-mp="25vt4yzoqe5cy">
                    <span className="font-semibold" data-id="element-3511" data-mp="25t70j8x505yy">
                      If still born or death? Cause:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.causeOfDeath}
                  </div>
                  <div className="col-span-2 flex space-x-8" data-id="element-3512" data-mp="21tg00dht6bh8">
                    <div data-id="element-3513" data-mp="21vsqxg5jnw6f">
                      <span className="font-semibold" data-id="element-3514" data-mp="1n4s4g77yq1ra">Apgar 1 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar1}
                    </div>
                    <div data-id="element-3515" data-mp="1rwtinyooy2jy">
                      <span className="font-semibold" data-id="element-3516" data-mp="1e43v3ptikpkq">Apgar 5 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar5}
                    </div>
                    <div data-id="element-3517" data-mp="1i5yrqh0i2es6">
                      <span className="font-semibold" data-id="element-3518" data-mp="145xqh0gbp5c6">Apgar 10 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar10}
                    </div>
                  </div>
                  <div data-id="element-3519" data-mp="196vc5n0jkhif">
                    <span className="font-semibold" data-id="element-3520" data-mp="pipcjrvmqlwi">General Exam:</span>{' '}
                    {selectedFile.newbornEvaluation.generalExam}
                  </div>
                  <div data-id="element-3521" data-mp="kmif6nhauiq6">
                    <span className="font-semibold" data-id="element-3522" data-mp="zh56e2m1k99p">
                      Congenital Malformation:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.congenitalMalformation}
                  </div>
                  <div className="col-span-2" data-id="element-3523" data-mp="18h1ofldxe9vs">
                    <span className="font-semibold" data-id="element-3524" data-mp="18fqlfnsb165q">If Yes Describe:</span>{' '}
                    {selectedFile.newbornEvaluation.congenitalMalformationDesc}
                  </div>
                  <div data-id="element-3525" data-mp="1i9z83uunm4vz">
                    <span className="font-semibold" data-id="element-3526" data-mp="1d8thq3ogutpm">Infant ARVS?:</span>{' '}
                    {selectedFile.newbornEvaluation.infantARVs}
                  </div>
                  <div data-id="element-3527" data-mp="1r8ug9ch9dp1x">
                    <span className="font-semibold" data-id="element-3528" data-mp="1r68btlpnzpnx">Inf Feeding:</span>{' '}
                    {selectedFile.newbornEvaluation.infantFeeding}
                  </div>
                  <div className="col-span-2" data-id="element-3529" data-mp="2tlf9jakl6syo">
                    <span className="font-semibold" data-id="element-3530" data-mp="2ykrzi46r8g5h">
                      RESUSCITATION METHODS:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.resuscitationMethods}
                  </div>
                </div>

                <div className="mt-4" data-id="element-3531" data-mp="37l6lz6vgg1ak">
                  <h4 className="font-bold text-sm mb-1 underline" data-id="element-3532" data-mp="2avftfd6q4o7e">
                    NEWBORN EXAMINATION
                  </h4>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3533" data-mp="2ftqxt8e926i8">
                    <div data-id="element-3534" data-mp="2jw5h1tltz8a1">
                      <span className="font-semibold" data-id="element-3535" data-mp="2jvvpi1l50t28">CROWN HEEL LENGTH:</span>{' '}
                      {selectedFile.newbornEvaluation.crownHeelLength}
                    </div>
                    <div data-id="element-3536" data-mp="1s57vralsmpyl">
                      <span className="font-semibold" data-id="element-3537" data-mp="1s3wrl9ofrg6y">HEAD CIRCUMFERENCE:</span>{' '}
                      {selectedFile.newbornEvaluation.headCircumference}
                    </div>
                    <div data-id="element-3538" data-mp="1onabzmw5vchq">
                      <span className="font-semibold" data-id="element-3539" data-mp="1olqrtl7dwhao">Skin: Jaundice:</span>{' '}
                      {selectedFile.newbornEvaluation.skinJaundice}
                    </div>
                    <div data-id="element-3540" data-mp="1flkmimluanml">
                      <span className="font-semibold" data-id="element-3541" data-mp="1fkso740uybsm">Other:</span>{' '}
                      {selectedFile.newbornEvaluation.skinOther}
                    </div>
                    <div data-id="element-3542" data-mp="15oppk857svaf">
                      <span className="font-semibold" data-id="element-3543" data-mp="15oy2g01c80lm">Fontanelles:</span>{' '}
                      {selectedFile.newbornEvaluation.fontanelles}
                    </div>
                    <div data-id="element-3544" data-mp="vxuzstsrfdk8">
                      <span className="font-semibold" data-id="element-3545" data-mp="vz64qwxxyrcx">Eyes:</span>{' '}
                      {selectedFile.newbornEvaluation.eyes} |{' '}
                      <span className="font-semibold" data-id="element-3546" data-mp="ruycwqw6svah">Treatment:</span>{' '}
                      {selectedFile.newbornEvaluation.eyesTreatment}
                    </div>
                    <div data-id="element-3547" data-mp="hz3qdkoueg2f">
                      <span className="font-semibold" data-id="element-3548" data-mp="i0oi72jrvkwl">Mouth:</span>{' '}
                      {selectedFile.newbornEvaluation.mouth}
                    </div>
                    <div data-id="element-3549" data-mp="3hfe9zh647zwi">
                      <span className="font-semibold" data-id="element-3550" data-mp="3hf4mc08jm4tz">Neck:</span>{' '}
                      {selectedFile.newbornEvaluation.neck}
                    </div>
                    <div data-id="element-3551" data-mp="37j18vy88qjrq">
                      <span className="font-semibold" data-id="element-3552" data-mp="37jt5ncdkqnjl">Chest:</span>{' '}
                      {selectedFile.newbornEvaluation.chest}
                    </div>
                    <div data-id="element-3553" data-mp="40zut7c6kct4">
                      <span className="font-semibold" data-id="element-3554" data-mp="42kmmp741hna">Abdomen:</span>{' '}
                      {selectedFile.newbornEvaluation.abdomen}
                    </div>
                    <div data-id="element-3555" data-mp="3q8zrh89nzv9m">
                      <span className="font-semibold" data-id="element-3556" data-mp="3q9j181p5vdck">Umbilicus:</span>{' '}
                      {selectedFile.newbornEvaluation.umbilicus}
                    </div>
                    <div data-id="element-3557" data-mp="2etkkdbnqrn0s">
                      <span className="font-semibold" data-id="element-3558" data-mp="2etb2ycgrio6p">Genitalia:</span>{' '}
                      {selectedFile.newbornEvaluation.genitalia} |{' '}
                      <span className="font-semibold" data-id="element-3559" data-mp="u9gts1jt78q1">Urine:</span>{' '}
                      {selectedFile.newbornEvaluation.urine}
                    </div>
                    <div data-id="element-3560" data-mp="148ogvggcihqc">
                      <span className="font-semibold" data-id="element-3561" data-mp="148eq3qnh86jl">Anus:</span>{' '}
                      {selectedFile.newbornEvaluation.anus}
                    </div>
                    <div data-id="element-3562" data-mp="1e02chd56exe7">
                      <span className="font-semibold" data-id="element-3563" data-mp="1dyr93efn7rnm">Meconium:</span>{' '}
                      {selectedFile.newbornEvaluation.meconium}
                    </div>
                    <div data-id="element-3564" data-mp="1mzpea01ti57s">
                      <span className="font-semibold" data-id="element-3565" data-mp="1n18s7k00446e">Upper Limbs:</span>{' '}
                      {selectedFile.newbornEvaluation.upperLimbs}
                    </div>
                    <div data-id="element-3566" data-mp="3oru5187kywgx">
                      <span className="font-semibold" data-id="element-3567" data-mp="3oslxwb9smg3i">ERB Paralysis:</span>{' '}
                      {selectedFile.newbornEvaluation.erbParalysis}
                    </div>
                    <div data-id="element-3568" data-mp="3lpft54xws715">
                      <span className="font-semibold" data-id="element-3569" data-mp="3lr0r74jfm83r">Lower Limbs:</span>{' '}
                      {selectedFile.newbornEvaluation.lowerLimbs}
                    </div>
                    <div data-id="element-3570" data-mp="3vnvib2ddl680">
                      <span className="font-semibold" data-id="element-3571" data-mp="3vp6mh3aqgfzn">Ortolani Sign:</span>{' '}
                      {selectedFile.newbornEvaluation.ortolaniSign}
                    </div>
                    <div className="col-span-2" data-id="element-3572" data-mp="8ixljbvt80lh">
                      <span className="font-semibold" data-id="element-3573" data-mp="8hmijea6uwvf">Back:</span>{' '}
                      {selectedFile.newbornEvaluation.back}
                    </div>
                    <div className="col-span-2 flex space-x-8" data-id="element-3574" data-mp="i9sborc6fkc7">
                      <span className="font-semibold" data-id="element-3575" data-mp="ib3aecqrm1wg">Reflexes:</span>
                      <div data-id="element-3576" data-mp="dapd9pqpccd7">
                        <span className="font-semibold" data-id="element-3577" data-mp="dcibqhr5vqa4">Grasp:</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesGrasp}
                      </div>
                      <div data-id="element-3578" data-mp="n9wnw561xswm">
                        <span className="font-semibold" data-id="element-3579" data-mp="n6iqlbc8w9w1">Moro (Startle):</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesMoro}
                      </div>
                      <div data-id="element-3580" data-mp="1auxleu3i265h">
                        <span className="font-semibold" data-id="element-3581" data-mp="wvxwrmfzlr9b">Sucking:</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesSucking}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4" data-id="element-3582" data-mp="n5ctvmta60kl">
                  <span className="font-semibold" data-id="element-3583" data-mp="n4bej658erx2">NEWBORN PROGRESS NOTES:</span>
                  <p className="mt-1 whitespace-pre-wrap" data-id="element-3584" data-mp="ry5zecwkpcxq">
                    {selectedFile.newbornEvaluation.progressNotes}
                  </p>
                </div>
              </div>

              {/* POST DELIVERY OBSERVATION CHART */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3585" data-mp="46s4bz71f392">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3586" data-mp="96dfd7zhf225">
                  POST DELIVERY OBSERVATION CHART
                </h3>
                <table className="w-full text-[10px] border-collapse border border-black text-center" data-id="element-3587" data-mp="1cg93i4b08qzt">
                  <thead data-id="element-3588" data-mp="17j8yzxtszwat">
                    <tr className="bg-gray-100" data-id="element-3589" data-mp="17izgswf02tfo">
                      <th className="border border-black p-1" colSpan={2} data-id="element-3590" data-mp="12qxvym3rnue1">
                        TIME
                      </th>
                      <th className="border border-black p-1" colSpan={8} data-id="element-3591" data-mp="xrlcmbc3t5g7">
                        MOTHER
                      </th>
                      <th className="border border-black p-1" colSpan={7} data-id="element-3592" data-mp="sqyo6rf68828">
                        BABY
                      </th>
                      <th className="border border-black p-1" rowSpan={2} data-id="element-3593" data-mp="js3h7d4aytxv">
                        SIGN
                      </th>
                    </tr>
                    <tr className="bg-gray-100" data-id="element-3594" data-mp="evd6747cuojb">
                      <th className="border border-black p-1" data-id="element-3595" data-mp="2e760gt2rdfsc">DATE</th>
                      <th className="border border-black p-1" data-id="element-3596" data-mp="297jr4uq1wjot">TIME</th>
                      <th className="border border-black p-1" data-id="element-3597" data-mp="298cnr2vapst8">BP</th>
                      <th className="border border-black p-1" data-id="element-3598" data-mp="2gq14xd4ymknl">PULSE</th>
                      <th className="border border-black p-1" data-id="element-3599" data-mp="2lpvvffktp884">RESPS</th>
                      <th className="border border-black p-1" data-id="element-3600" data-mp="2lo2os0a7mo04">TEMP</th>
                      <th className="border border-black p-1" data-id="element-3601" data-mp="2qhdwe3muj8t3">PV BLEEDING</th>
                      <th className="border border-black p-1" data-id="element-3602" data-mp="2vex92z3wbdix">UTERUS</th>
                      <th className="border border-black p-1" data-id="element-3603" data-mp="1xsgfa3udfi1x">BLADDER</th>
                      <th className="border border-black p-1" data-id="element-3604" data-mp="1xuaxhae9cw2b">PALLOR</th>
                      <th className="border border-black p-1" data-id="element-3605" data-mp="22rtapczb5xg4">TEMP</th>
                      <th className="border border-black p-1" data-id="element-3606" data-mp="27q4jrlhs43xa">RESPS</th>
                      <th className="border border-black p-1" data-id="element-3607" data-mp="2cph49zl6gywp">CORD</th>
                      <th className="border border-black p-1" data-id="element-3608" data-mp="2cne78vlly5ih">SKIN COLOUR</th>
                      <th className="border border-black p-1" data-id="element-3609" data-mp="3j4rnhl3v5a5a">
                        FEEDING (BF/AF)
                      </th>
                      <th className="border border-black p-1" data-id="element-3610" data-mp="3o6gy4zn6blsu">BOWEL</th>
                      <th className="border border-black p-1" data-id="element-3611" data-mp="1o0tinuwa2ty8">URINE</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-3612" data-mp="1j5e759lqta4z">
                    {selectedFile.postDeliveryObservations.length > 0 ? selectedFile.postDeliveryObservations.map(obs => <tr key={obs.id} data-id="element-3613" data-mp="1t1zdby9i0rae">
                          <td className="border border-black p-1" data-id="element-3614" data-mp="15bn9ozladgpm">
                            {obs.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-3615" data-mp="10dv9liahmoac">
                            {obs.time}
                          </td>
                          <td className="border border-black p-1" data-id="element-3616" data-mp="1e6beypqv2yef">{obs.bp}</td>
                          <td className="border border-black p-1" data-id="element-3617" data-mp="19bxgufkted62">
                            {obs.pulse}
                          </td>
                          <td className="border border-black p-1" data-id="element-3618" data-mp="hmapl4of7o8j">
                            {obs.resps}
                          </td>
                          <td className="border border-black p-1" data-id="element-3619" data-mp="7hchrqvbaf2m">
                            {obs.temp}
                          </td>
                          <td className="border border-black p-1" data-id="element-3620" data-mp="cb6z4nng2hyj">
                            {obs.pvBleeding}
                          </td>
                          <td className="border border-black p-1" data-id="element-3621" data-mp="2iripursq1fr">
                            {obs.uterus}
                          </td>
                          <td className="border border-black p-1" data-id="element-3622" data-mp="q9er0h5mit2u">
                            {obs.bladder}
                          </td>
                          <td className="border border-black p-1" data-id="element-3623" data-mp="gapk1jf9mt1d">
                            {obs.pallor}
                          </td>
                          <td className="border border-black p-1" data-id="element-3624" data-mp="l8r4o8scb0ey">
                            {obs.babyTemp}
                          </td>
                          <td className="border border-black p-1" data-id="element-3625" data-mp="313125jvtu86h">
                            {obs.babyResps}
                          </td>
                          <td className="border border-black p-1" data-id="element-3626" data-mp="2n9qp8e6vh0xw">
                            {obs.cord}
                          </td>
                          <td className="border border-black p-1" data-id="element-3627" data-mp="2i92oh0sud5qh">
                            {obs.skinColour}
                          </td>
                          <td className="border border-black p-1" data-id="element-3628" data-mp="299z65z1i0ud1">
                            {obs.feedingBF} / {obs.feedingAF}
                          </td>
                          <td className="border border-black p-1" data-id="element-3629" data-mp="35z6qj3qdsxfd">
                            {obs.eliminationBowel}
                          </td>
                          <td className="border border-black p-1" data-id="element-3630" data-mp="2w9n63tn0svie">
                            {obs.eliminationUrine}
                          </td>
                          <td className="border border-black p-1" data-id="element-3631" data-mp="2s8bg3n08i89o">
                            {obs.sign}
                          </td>
                        </tr>) : <tr data-id="element-3632" data-mp="3jxwosmoxt35b">
                        <td colSpan={18} className="border border-black p-4 text-gray-400 italic" data-id="element-3633" data-mp="16hd74ncm5wbi">
                          No observations recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DRUG CHART */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3634" data-mp="1pghd1n8uz9yy">
                <h3 className="font-bold uppercase underline mb-2 text-center" data-id="element-3635" data-mp="1uf2dnns0vvnx">
                  DRUG CHART
                </h3>
                <div className="flex justify-between mb-2 text-xs" data-id="element-3636" data-mp="1z5ior18df574">
                  <div data-id="element-3637" data-mp="237cn32v3fx44">
                    <span className="font-semibold" data-id="element-3638" data-mp="237vxlyiezj84">NAME:</span>{' '}
                    {selectedFile.patient.firstName}{' '}
                    {selectedFile.patient.surname}
                  </div>
                  <div data-id="element-3639" data-mp="2d4qsm7fhb1hn">
                    <span className="font-semibold" data-id="element-3640" data-mp="2d6boc0dj4qh3">WARD:</span>{' '}
                    {selectedFile.drugChart?.ward}
                  </div>
                  <div data-id="element-3641" data-mp="3ifrcoq320q2m">
                    <span className="font-semibold" data-id="element-3642" data-mp="3igalnhaq844i">No.:</span>{' '}
                    {selectedFile.drugChart?.patientNo}
                  </div>
                  <div data-id="element-3643" data-mp="34huc7ilhbzio">
                    <span className="font-semibold" data-id="element-3644" data-mp="39f2xvt5u6loo">SEX/AGE:</span> Female /{' '}
                    {selectedFile.patient.age}
                  </div>
                </div>

                <table className="w-full text-[9px] border-collapse border border-black text-center" data-id="element-3645" data-mp="102q2ku0y9se">
                  <thead data-id="element-3646" data-mp="5038sho5p759">
                    <tr className="bg-gray-100" data-id="element-3647" data-mp="3nevkpde8shrd">
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3648" data-mp="3ne2jst1ysmh5">
                        DATE
                      </th>
                      <th className="border border-black p-1 w-24" rowSpan={3} data-id="element-3649" data-mp="3sbm1y41s9jed">
                        DRUG
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3650" data-mp="op3s9qc9kop8">
                        DOSAGE
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3651" data-mp="y1uiuh8eurhn">
                        ROUTE OF ADMIN
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3652" data-mp="17t77kzywx1jg">
                        ORDERED BY
                      </th>
                      <th className="border border-black p-1" colSpan={21} data-id="element-3653" data-mp="12uxf4x4kof1j">
                        SUPPLIED BY (DATE)
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3654" data-mp="b37tyr3wncu0">
                        SUPPLIED BY
                      </th>
                      <th className="border border-black p-1 w-12" rowSpan={3} data-id="element-3655" data-mp="p3sb7iu690le">
                        QUANTITY
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3656" data-mp="k68xql5asrui">
                        DATE
                      </th>
                    </tr>
                    <tr className="bg-gray-100" data-id="element-3657" data-mp="1vi9r6dd3x4gl">
                      {[0, 1, 2].map(colIdx => <th key={colIdx} className="border border-black p-1" colSpan={7} data-id="element-3658" data-mp="23izbcmxfdwch">
                          {selectedFile.drugChart?.supplyDates?.[colIdx] || ''}
                        </th>)}
                    </tr>
                    <tr className="bg-gray-100" data-id="element-3659" data-mp="1jvrqc69s1spf">
                      {[0, 1, 2].map(colIdx => <Fragment key={`times-${colIdx}`} data-id="element-3660" data-mp="1oul7q9s6q3uv">
                          {['6', '10', '12', '14', '18', '22', '24'].map(time => <th key={`${colIdx}-${time}`} className="border border-black p-0.5 font-normal" data-id="element-3661" data-mp="1ts4lz9ovugmt">
                                {time}
                              </th>)}
                        </Fragment>)}
                    </tr>
                  </thead>
                  <tbody data-id="element-3662" data-mp="tlhng4ix6qo7">
                    {selectedFile.drugChart?.rows && selectedFile.drugChart.rows.length > 0 ? selectedFile.drugChart.rows.map(row => <tr key={row.id} data-id="element-3663" data-mp="jmjsojuwi0wr">
                          <td className="border border-black p-1" data-id="element-3664" data-mp="jpxrji4cvrzg">
                            {row.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-3665" data-mp="auzwa4dqk34y">
                            {row.drug}
                          </td>
                          <td className="border border-black p-1" data-id="element-3666" data-mp="5wopk2iqm8qy">
                            {row.dosage}
                          </td>
                          <td className="border border-black p-1" data-id="element-3667" data-mp="3s3dczo40br8y">
                            {row.routeOfAdmin}
                          </td>
                          <td className="border border-black p-1" data-id="element-3668" data-mp="3o32x9jqexmgw">
                            {row.orderedBy}
                          </td>
                          {[0, 1, 2].map(colIdx => <Fragment key={`admin-${colIdx}`} data-id="element-3669" data-mp="lkth30s2aku7">
                              {['6', '10', '12', '14', '18', '22', '24'].map(time => {
                      const key = `${colIdx}-${time}`;
                      return <td key={key} className="border border-black p-0.5" data-id="element-3670" data-mp="3u16h30roysre">
                                      {row.administration?.[key] || ''}
                                    </td>;
                    })}
                            </Fragment>)}
                          <td className="border border-black p-1" data-id="element-3671" data-mp="1nbrunv7y4tlz">
                            {row.suppliedBy}
                          </td>
                          <td className="border border-black p-1" data-id="element-3672" data-mp="37rg4pjqwjw3c">
                            {row.quantity}
                          </td>
                          <td className="border border-black p-1" data-id="element-3673" data-mp="3hjvngja0wopa">
                            {row.suppliedDate}
                          </td>
                        </tr>) : <tr data-id="element-3674" data-mp="3rggv7cdfgdwt">
                        <td colSpan={30} className="border border-black p-4 text-gray-400 italic" data-id="element-3675" data-mp="3rfoy1x46m84f">
                          No drug chart recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
                <div className="flex justify-between mt-4 text-xs" data-id="element-3676" data-mp="263nhs8zaox9e">
                  <div data-id="element-3677" data-mp="26607577du9wh">
                    <span className="font-semibold" data-id="element-3678" data-mp="215lypo1vcxxx">DATE:</span>{' '}
                    {selectedFile.drugChart?.signedDate}
                  </div>
                  <div data-id="element-3679" data-mp="53sb2887fpf0">
                    <span className="font-semibold" data-id="element-3680" data-mp="54ah1wko5i04">SIGNATURE:</span>{' '}
                    {selectedFile.drugChart?.signedBy}
                  </div>
                </div>
              </div>

              {/* PUERPERIUM NOTES */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3681" data-mp="j1z1x4eqkp56">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3682" data-mp="o0tli8kj2hqc">
                  PUERPERIUM NOTES
                </h3>
                <table className="w-full text-sm border-collapse border border-black" data-id="element-3683" data-mp="sz4tedr9ii5x">
                  <thead data-id="element-3684" data-mp="xq3b9hrwfoba">
                    <tr className="bg-gray-100" data-id="element-3685" data-mp="xrxwyyazvkgf">
                      <th className="border border-black p-2 text-left w-32" data-id="element-3686" data-mp="12ppzsnd6gqze">
                        DATE
                      </th>
                      <th className="border border-black p-2 text-left" data-id="element-3687" data-mp="16s2v0huat6hx">
                        NOTES
                      </th>
                      <th className="border border-black p-2 text-left w-48" data-id="element-3688" data-mp="1bqxivy74hl8w">
                        SIGNATURE
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-3689" data-mp="2r8kh9muzfqot">
                    {selectedFile.puerperiumNotes.length > 0 ? selectedFile.puerperiumNotes.map(note => <tr key={note.id} data-id="element-3690" data-mp="2i973ihztsfya">
                          <td className="border border-black p-2" data-id="element-3691" data-mp="2ia8hau887gjp">
                            {note.date}
                          </td>
                          <td className="border border-black p-2 whitespace-pre-wrap" data-id="element-3692" data-mp="3axb9l4mb6v1i">
                            {note.notes}
                          </td>
                          <td className="border border-black p-2" data-id="element-3693" data-mp="3651r29niwx99">
                            {note.signature}
                          </td>
                        </tr>) : <tr data-id="element-3694" data-mp="2jm9g772cb1m">
                        <td colSpan={3} className="border border-black p-4 text-gray-400 italic text-center" data-id="element-3695" data-mp="3twguk5r1dkm3">
                          No notes recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DISCHARGE SLIP */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3696" data-mp="2x8zvgyuoh4h0">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-3697" data-mp="3kz3x50ob2u5d">
                  DISCHARGE SLIP FOR LABOUR WARD
                </h3>

                <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4 text-sm" data-id="element-3698" data-mp="3pwvrs2gc4ud9">
                  <div data-id="element-3699" data-mp="3bz76iticvl7o">
                    <span className="font-semibold" data-id="element-3700" data-mp="3bxvz8jppbvbt">SM No:</span>{' '}
                    {selectedFile.dischargeSlip?.smNo}
                  </div>
                  <div data-id="element-3701" data-mp="20jievou5py0j">
                    <span className="font-semibold" data-id="element-3702" data-mp="20i7d1uk9v0c2">Gravida:</span>{' '}
                    {selectedFile.dischargeSlip?.gravida}
                  </div>
                  <div data-id="element-3703" data-mp="24iixb68q2yn0">
                    <span className="font-semibold" data-id="element-3704" data-mp="1x0tfhzrboxf1">Patient's Name:</span>{' '}
                    {selectedFile.dischargeSlip?.patientName || `${selectedFile.patient.firstName} ${selectedFile.patient.surname}`}
                  </div>
                  <div data-id="element-3705" data-mp="26yqzgb25g81u">
                    <span className="font-semibold" data-id="element-3706" data-mp="220fr64ri65lq">Party:</span>{' '}
                    {selectedFile.dischargeSlip?.party}
                  </div>
                  <div data-id="element-3707" data-mp="1ebmug6323tq2">
                    <span className="font-semibold" data-id="element-3708" data-mp="1e8qukc7neuyz">Time of Discharge:</span>{' '}
                    {selectedFile.dischargeSlip?.timeOfDischarge}
                  </div>
                  <div data-id="element-3709" data-mp="1o1g4v3rgq2sq">
                    <span className="font-semibold" data-id="element-3710" data-mp="1o1zdtuz4xgum">Age:</span>{' '}
                    {selectedFile.dischargeSlip?.age || selectedFile.patient.age}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3711" data-mp="vi0roni2v864">
                  Labour and Delivery Summary
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4 text-sm border border-black p-3" data-id="element-3712" data-mp="28tfxmxw05a3r">
                  <div className="font-bold uppercase" data-id="element-3713" data-mp="23ucq96sjursm">Mother</div>
                  <div className="font-bold uppercase" data-id="element-3714" data-mp="2hkg8lmob61cs">Infant</div>
                  <div data-id="element-3715" data-mp="2hmsxykwebdzv">
                    <span className="font-semibold" data-id="element-3716" data-mp="2ctzqvqdgftkm">D.O.B:</span>{' '}
                    {selectedFile.dischargeSlip?.motherDob}
                  </div>
                  <div data-id="element-3717" data-mp="2rhb3lvldhjmb">
                    <span className="font-semibold" data-id="element-3718" data-mp="2rkywcsizu1zz">T.O.B:</span>{' '}
                    {selectedFile.dischargeSlip?.infantTob}
                  </div>
                  <div data-id="element-3719" data-mp="30ij1icjz8ifv">
                    <span className="font-semibold" data-id="element-3720" data-mp="30j0y5aajwyob">M.O.D:</span>{' '}
                    {selectedFile.dischargeSlip?.modeOfDelivery}
                  </div>
                  <div data-id="element-3721" data-mp="164zxqkzcs3m4">
                    <span className="font-semibold" data-id="element-3722" data-mp="166b0chh2b5bn">A/S:</span>{' '}
                    {selectedFile.dischargeSlip?.apgarScore}
                  </div>
                  <div data-id="element-3723" data-mp="rzzjylth7ev4">
                    <span className="font-semibold" data-id="element-3724" data-mp="rzr7uw56gdkz">Perineum:</span>{' '}
                    {selectedFile.dischargeSlip?.perineum}
                  </div>
                  <div data-id="element-3725" data-mp="w3pbnk5g6ckd">
                    <span className="font-semibold" data-id="element-3726" data-mp="iag13g5rjl09">Sex:</span>{' '}
                    {selectedFile.dischargeSlip?.infantSex}
                  </div>
                  <div className="col-span-2" data-id="element-3727" data-mp="n7q1fquoxh2v">
                    <span className="font-semibold" data-id="element-3728" data-mp="8c9zz0rs6iwg">Comments:</span>{' '}
                    {selectedFile.dischargeSlip?.comments}
                  </div>
                  <div className="col-span-2" data-id="element-3729" data-mp="dcekr2zq1zsh">
                    <span className="font-semibold" data-id="element-3730" data-mp="3vh9fp3ky9kle">P.C.A:</span>{' '}
                    {selectedFile.dischargeSlip?.pca}
                  </div>
                </div>

                <div className="flex justify-between items-center mb-2" data-id="element-3731" data-mp="4c9onozzantm">
                  <h4 className="font-bold uppercase underline text-sm" data-id="element-3732" data-mp="1tpwb6fmw9epv">
                    Discharge Summary
                  </h4>
                  <div className="text-sm" data-id="element-3733" data-mp="1w9t0fvvwykg1">
                    <span className="font-semibold" data-id="element-3734" data-mp="1wau9xvxa6yvn">Date of Discharge:</span>{' '}
                    {selectedFile.dischargeSlip?.dateOfDischarge}
                  </div>
                </div>

                <div className="border border-black p-3 mb-4 text-sm" data-id="element-3735" data-mp="1na5xh5njd6kv">
                  <h5 className="font-bold uppercase mb-2" data-id="element-3736" data-mp="1ia9rj0va352o">Mother</h5>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3737" data-mp="1ic4gqrdl1jck">
                    <div data-id="element-3738" data-mp="2fwgyghf219i7">
                      <span className="font-semibold" data-id="element-3739" data-mp="2fzuyvk45r8n0">BP:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.bp}
                    </div>
                    <div data-id="element-3740" data-mp="266xch2w1ozj4">
                      <span className="font-semibold" data-id="element-3741" data-mp="266e2q9gjthg6">Pallor:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.pallor}
                    </div>
                    <div data-id="element-3742" data-mp="2yojg9w3fmvu1">
                      <span className="font-semibold" data-id="element-3743" data-mp="2ypul7z8m69mq">Breast:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.breast}
                    </div>
                    <div data-id="element-3744" data-mp="3197tc0zt4uzx">
                      <span className="font-semibold" data-id="element-3745" data-mp="319zjuxejs2jc">
                        Breastfeeding (Yes/No):
                      </span>{' '}
                      {selectedFile.dischargeSlip?.mother?.breastfeeding}
                    </div>
                    <div data-id="element-3746" data-mp="3b98bhjlxx52j">
                      <span className="font-semibold" data-id="element-3747" data-mp="3g6hzcutol8o9">Uterus:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.uterus}
                    </div>
                    <div data-id="element-3748" data-mp="3k5h9ctu9rvmk">
                      <span className="font-semibold" data-id="element-3749" data-mp="3p6dfzf609vvo">Perineum:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.perineum}
                    </div>
                    <div data-id="element-3750" data-mp="3tzg95m6vf3bc">
                      <span className="font-semibold" data-id="element-3751" data-mp="2qsnt68mrg3s">Lochia:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.lochia}
                    </div>
                    <div data-id="element-3752" data-mp="7o2awf8jrfog">
                      <span className="font-semibold" data-id="element-3753" data-mp="brqmd90auvez">Bowels:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.bowels}
                    </div>
                    <div data-id="element-3754" data-mp="19eshx8hi5tsk">
                      <span className="font-semibold" data-id="element-3755" data-mp="14hjx104yzbnm">Urine:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.urine}
                    </div>
                    <div data-id="element-3756" data-mp="1i8fbcry8mkyl">
                      <span className="font-semibold" data-id="element-3757" data-mp="1iarmom9hpx2o">Family Planning:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.familyPlanning}
                    </div>
                    <div className="col-span-2" data-id="element-3758" data-mp="qi10l4litor5">
                      <span className="font-semibold" data-id="element-3759" data-mp="lj7p1hpso7tm">Danger Signs:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.dangerSigns}
                    </div>
                    <div className="col-span-2" data-id="element-3760" data-mp="ujcjkr9w3bqd">
                      <span className="font-semibold" data-id="element-3761" data-mp="uikzodpnn2fx">Treatment:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.treatment}
                    </div>
                    <div data-id="element-3762" data-mp="25z183n2skita">
                      <span className="font-semibold" data-id="element-3763" data-mp="25xq2dhpsd0zj">Vitamin A:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.vitaminA}
                    </div>
                  </div>
                </div>

                <div className="border border-black p-3 mb-4 text-sm" data-id="element-3764" data-mp="2iigaaztb0a20">
                  <h5 className="font-bold uppercase mb-2" data-id="element-3765" data-mp="24idvjmgalwqz">Infant</h5>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3766" data-mp="29g6z07q7ocnk">
                    <div data-id="element-3767" data-mp="29ijiipbmav2q">
                      <span className="font-semibold" data-id="element-3768" data-mp="2x69ruq1y9mn8">BWT:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.birthWeight}
                    </div>
                    <div data-id="element-3769" data-mp="327764rju9wjg">
                      <span className="font-semibold" data-id="element-3770" data-mp="2nghszjmti7cy">Discharge Weight:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.dischargeWeight}
                    </div>
                    <div data-id="element-3771" data-mp="2scyhiexxinqk">
                      <span className="font-semibold" data-id="element-3772" data-mp="3g4dlsd99nf4g">Pallor:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.pallor}
                    </div>
                    <div data-id="element-3773" data-mp="3kxf5qzoby4uw">
                      <span className="font-semibold" data-id="element-3774" data-mp="3urcfkevlilmc">Jaundice:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.jaundice}
                    </div>
                    <div className="col-span-2" data-id="element-3775" data-mp="3pqeyy6q8hzmy">
                      <span className="font-semibold" data-id="element-3776" data-mp="3ksn2czen9pcf">
                        Passed Meconium / Urine:
                      </span>{' '}
                      {selectedFile.dischargeSlip?.infant?.passedMeconiumUrine}
                    </div>
                    <div data-id="element-3777" data-mp="3but9jyg39du0">
                      <span className="font-semibold" data-id="element-3778" data-mp="3729x54t50oqs">Feeding:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.feeding}
                    </div>
                    <div data-id="element-3779" data-mp="3236ufr0mqulz">
                      <span className="font-semibold" data-id="element-3780" data-mp="2x3u7z7do7pjx">Feeding Option:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.feedingOption}
                    </div>
                    <div data-id="element-3781" data-mp="2t4bnudtobii5">
                      <span className="font-semibold" data-id="element-3782" data-mp="2o3e6g3ghmshp">Breast Feeding:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.breastFeeding}
                    </div>
                    <div data-id="element-3783" data-mp="13nfy842c2cjk">
                      <span className="font-semibold" data-id="element-3784" data-mp="18jol63mrva1a">BCG:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.bcg}
                    </div>
                    <div data-id="element-3785" data-mp="1cjoz7n5ylj7t">
                      <span className="font-semibold" data-id="element-3786" data-mp="1hkmi61ysmhad">OPV:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.opv}
                    </div>
                    <div data-id="element-3787" data-mp="kwgiltgqaf2y">
                      <span className="font-semibold" data-id="element-3788" data-mp="ow73jgjkq0zk">Birth Record:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.birthRecord}
                    </div>
                    <div data-id="element-3789" data-mp="ttz40yyaavfd">
                      <span className="font-semibold" data-id="element-3790" data-mp="ym0fwjrjik4v">G/C:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.gc}
                    </div>
                    <div className="col-span-2" data-id="element-3791" data-mp="163c07jntrox">
                      <span className="font-semibold" data-id="element-3792" data-mp="65fzmuicv2sk">Treatment:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.treatment}
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm" data-id="element-3793" data-mp="2qphqp47odjda">
                  <div data-id="element-3794" data-mp="2qqswf9kol171">
                    <span className="font-semibold" data-id="element-3795" data-mp="34o7okm2bjn2p">
                      6 days Postnatal Date:
                    </span>{' '}
                    {selectedFile.dischargeSlip?.sixDaysPostnatalDate}
                  </div>
                  <div data-id="element-3796" data-mp="3elvm1jrb78ok">
                    <span className="font-semibold" data-id="element-3797" data-mp="3el2mb2qrpjfx">
                      6 Weeks Postnatal Date:
                    </span>{' '}
                    {selectedFile.dischargeSlip?.sixWeeksPostnatalDate}
                  </div>
                  <div data-id="element-3798" data-mp="3oe9nebeh6da7">
                    <span className="font-semibold" data-id="element-3799" data-mp="3jdlzvzpggekq">Name in Full:</span>{' '}
                    {selectedFile.dischargeSlip?.nameInFull}
                  </div>
                </div>
              </div>

              {/* POSTNATAL AT ONE WEEK */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3800" data-mp="3sfmvks51iska">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-3801" data-mp="fvhd9kgdbmmw">
                  Postnatal at One Week
                </h3>
                <div className="text-sm mb-3" data-id="element-3802" data-mp="krg73nkgtwup">
                  <span className="font-semibold" data-id="element-3803" data-mp="6uj82yu7192q">Date:</span>{' '}
                  {selectedFile.postNatalOneWeek?.date}
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3804" data-mp="atjqig8re9p7">
                  Mother
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-4" data-id="element-3805" data-mp="1uog0ryomdg3">
                  <div data-id="element-3806" data-mp="1wriibh0tz1p">
                    <span className="font-semibold" data-id="element-3807" data-mp="3jbg41z2nuekh">General Well Being:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.generalWellBeing}
                  </div>
                  <div data-id="element-3808" data-mp="3o7wpumme0gue">
                    <span className="font-semibold" data-id="element-3809" data-mp="3a9yelq3d50j4">Temp:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.temp}
                  </div>
                  <div data-id="element-3810" data-mp="3f8rwrvtlhfpm">
                    <span className="font-semibold" data-id="element-3811" data-mp="227jou4wcd2gc">Pulse:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.pulse}
                  </div>
                  <div data-id="element-3812" data-mp="269nblpogntgx">
                    <span className="font-semibold" data-id="element-3813" data-mp="26aygjstn779m">BP:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.bp}
                  </div>
                  <div data-id="element-3814" data-mp="2g8ciyoiqdvix">
                    <span className="font-semibold" data-id="element-3815" data-mp="2g8mc2kz2oisu">Urine:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.urine}
                  </div>
                  <div data-id="element-3816" data-mp="2p77qq51fbdoc">
                    <span className="font-semibold" data-id="element-3817" data-mp="2p6ohrdtr3zmg">Head:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.head}
                  </div>
                  <div data-id="element-3818" data-mp="2yxj5kmmnb7a0">
                    <span className="font-semibold" data-id="element-3819" data-mp="2ywradcwynbk9">Eyes:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.eyes}
                  </div>
                  <div data-id="element-3820" data-mp="33v2nbwijy26p">
                    <span className="font-semibold" data-id="element-3821" data-mp="38vgvrfo2fe59">Ears:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.ears}
                  </div>
                  <div data-id="element-3822" data-mp="x6k26cdyjczn">
                    <span className="font-semibold" data-id="element-3823" data-mp="s9jzmbgbgsda">Mouth:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.mouth}
                  </div>
                  <div data-id="element-3824" data-mp="naz4gqfx8yvp">
                    <span className="font-semibold" data-id="element-3825" data-mp="nca9etl3scoe">Neck:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.neck}
                  </div>
                  <div data-id="element-3826" data-mp="1fxte0sb7adue">
                    <span className="font-semibold" data-id="element-3827" data-mp="1fyczgj027kd6">Breasts:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.breasts}
                  </div>
                  <div data-id="element-3828" data-mp="1689udkfnyfyh">
                    <span className="font-semibold" data-id="element-3829" data-mp="167qjuoscetuh">Ampills:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.ampills}
                  </div>
                  <div data-id="element-3830" data-mp="1ypvxebf8888c">
                    <span className="font-semibold" data-id="element-3831" data-mp="1yqe09qwe9kp8">Abdomen (Uterus):</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.abdomenUterus}
                  </div>
                  <div data-id="element-3832" data-mp="3o6hqsfuxiass">
                    <span className="font-semibold" data-id="element-3833" data-mp="3o79obw836ilp">Groin:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.groin}
                  </div>
                  <div data-id="element-3834" data-mp="3ehgbkrw7jxfv">
                    <span className="font-semibold" data-id="element-3835" data-mp="3efws6sf9969v">Perineum:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.perineum}
                  </div>
                  <div data-id="element-3836" data-mp="35gi3nyinf5s0">
                    <span className="font-semibold" data-id="element-3837" data-mp="35f72m6gl8c4l">Legs:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.legs}
                  </div>
                  <div data-id="element-3838" data-mp="39fiofmkosihn">
                    <span className="font-semibold" data-id="element-3839" data-mp="2vilv9eh3ioxl">Anus:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.anus}
                  </div>
                  <div data-id="element-3840" data-mp="30j9lvz1ex3ra">
                    <span className="font-semibold" data-id="element-3841" data-mp="tqpjq4uxhf2t">Lochia:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.lochia}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3842" data-mp="yng2izy4aorx">
                  Vaginal Examination
                </h4>
                <div className="space-y-2 text-sm mb-4" data-id="element-3843" data-mp="2e99tnn9algb9">
                  <div data-id="element-3844" data-mp="2e6ng96zq06l4">
                    <span className="font-semibold" data-id="element-3845" data-mp="2sxe6ialnyxm4">Cervix per speculum:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3846" data-mp="2szh2db9hzkyr">
                      {selectedFile.postNatalOneWeek?.mother.cervixPerSpeculum}
                    </span>
                  </div>
                  <div data-id="element-3847" data-mp="320f7xxzl40jg">
                    <span className="font-semibold" data-id="element-3848" data-mp="2wzhsvu9vfmm6">Family Planning:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3849" data-mp="2x1lu24gdy7ir">
                      {selectedFile.postNatalOneWeek?.mother.familyPlanning}
                    </span>
                  </div>
                  <div data-id="element-3850" data-mp="36vchd60s0bwv">
                    <span className="font-semibold" data-id="element-3851" data-mp="1hihauhuz5a5s">Treatment or advise:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3852" data-mp="1hkbza65gfkem">
                      {selectedFile.postNatalOneWeek?.mother.treatmentOrAdvise}
                    </span>
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3853" data-mp="8lk9p5qlbsfd">
                  Infant Examination Form
                </h4>
                <div className="grid grid-cols-3 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-3854" data-mp="dlotp5qpj5ac">
                  <div data-id="element-3855" data-mp="hmqrdi4z79a7">
                    <span className="font-semibold" data-id="element-3856" data-mp="hkxngalqz35w">Infant:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.name}
                  </div>
                  <div data-id="element-3857" data-mp="3m074vgby30y6">
                    <span className="font-semibold" data-id="element-3858" data-mp="3m1i9tjh4meqv">Weight:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.weight}
                  </div>
                  <div data-id="element-3859" data-mp="3usqw3vpwq38z">
                    <span className="font-semibold" data-id="element-3860" data-mp="3utacv93tmllf">Date:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.date}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-3861" data-mp="32cg6zxdi6z3y">
                  <div data-id="element-3862" data-mp="32adaqvlrc9qs">
                    <span className="font-semibold" data-id="element-3863" data-mp="2usos0gwg39ub">Head Circumference:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.headCircumference}
                  </div>
                  <div data-id="element-3864" data-mp="2ls8s1hewuku7">
                    <span className="font-semibold" data-id="element-3865" data-mp="2lrqwyo3zicnv">Length:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.length}
                  </div>
                  <div data-id="element-3866" data-mp="2gva2lc66z22c">
                    <span className="font-semibold" data-id="element-3867" data-mp="2btt1bfjspdl8">Temp:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.temp}
                  </div>
                  <div data-id="element-3868" data-mp="2721ijr6vsc5n">
                    <span className="font-semibold" data-id="element-3869" data-mp="271jh8g5d37qv">Head:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.head}
                  </div>
                  <div data-id="element-3870" data-mp="1y3z8yn930b6r">
                    <span className="font-semibold" data-id="element-3871" data-mp="1y0l8jkjzac1y">Eyes:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.eyes}
                  </div>
                  <div data-id="element-3872" data-mp="1o5jj0nlsj7yu">
                    <span className="font-semibold" data-id="element-3873" data-mp="1o50bm0tro1z2">Ears:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.ears}
                  </div>
                  <div data-id="element-3874" data-mp="1jgiijnw6gogf">
                    <span className="font-semibold" data-id="element-3875" data-mp="1jgqundkh7pqk">Mouth:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.mouth}
                  </div>
                  <div data-id="element-3876" data-mp="19hji2slkfz4i">
                    <span className="font-semibold" data-id="element-3877" data-mp="19h01bf7njgs2">Neck:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.neck}
                  </div>
                  <div data-id="element-3878" data-mp="1x9qajgo67lyn">
                    <span className="font-semibold" data-id="element-3879" data-mp="22706zg9z8xvz">Chest:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.chest}
                  </div>
                  <div data-id="element-3880" data-mp="1nfhuvtk8nnhw">
                    <span className="font-semibold" data-id="element-3881" data-mp="1sgnodtlq3esh">Abdomen:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.abdomen}
                  </div>
                  <div data-id="element-3882" data-mp="2g4x5wjbwlgdt">
                    <span className="font-semibold" data-id="element-3883" data-mp="2g4ezwuzfvnsp">Cord:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.cord}
                  </div>
                  <div data-id="element-3884" data-mp="9goemxvh5ihq">
                    <span className="font-semibold" data-id="element-3885" data-mp="9fwjfo5shmrz">Genitalia & Rectum:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.genitaliaRectum}
                  </div>
                  <div data-id="element-3886" data-mp="j79c2hzewgz2">
                    <span className="font-semibold" data-id="element-3887" data-mp="j9vl6m1yb4je">Extremities:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.extremities}
                  </div>
                  <div data-id="element-3888" data-mp="3mp19wd08bv56">
                    <span className="font-semibold" data-id="element-3889" data-mp="3mqlupawqrzpu">Back:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.back}
                  </div>
                  <div data-id="element-3890" data-mp="3vq8trq200tfq">
                    <span className="font-semibold" data-id="element-3891" data-mp="3vqinnoq5zkqp">Neurologic Exam:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.neurologicExam}
                  </div>
                  <div data-id="element-3892" data-mp="1axnjpwgu0x2a">
                    <span className="font-semibold" data-id="element-3893" data-mp="1ax5gugznzkle">Feeding:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.feeding}
                  </div>
                </div>
                <div className="text-sm" data-id="element-3894" data-mp="30hnfva941aup">
                  <span className="font-semibold" data-id="element-3895" data-mp="35f6tq91wblm4">Additional Notes:</span>
                  <p className="whitespace-pre-wrap mt-1" data-id="element-3896" data-mp="35d2qzufqgsnf">
                    {selectedFile.postNatalOneWeek?.infant.additionalNotes}
                  </p>
                </div>
              </div>

              {/* POSTNATAL AT SIX WEEKS */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3897" data-mp="3j73iq489pfea">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-3898" data-mp="3o5ojc4rfm139">
                  Postnatal at Six Weeks
                </h3>
                <div className="text-sm mb-3" data-id="element-3899" data-mp="3t5t2y3nmzbxp">
                  <span className="font-semibold" data-id="element-3900" data-mp="3t2x48d3ysj87">Date:</span>{' '}
                  {selectedFile.postNatalSixWeeks?.date}
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3901" data-mp="1yrs3pho6cmx3">
                  Mother
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-4" data-id="element-3902" data-mp="23qd35evlr2kh">
                  <div data-id="element-3903" data-mp="kvqp32pu2laj">
                    <span className="font-semibold" data-id="element-3904" data-mp="kw05pzozng3k">General Well Being:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.generalWellBeing}
                  </div>
                  <div data-id="element-3905" data-mp="c36po01pwcon">
                    <span className="font-semibold" data-id="element-3906" data-mp="c0aqk8e4vhym">Temp:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.temp}
                  </div>
                  <div data-id="element-3907" data-mp="13sjjx3enfu6z">
                    <span className="font-semibold" data-id="element-3908" data-mp="13t1gk1584aff">Pulse:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.pulse}
                  </div>
                  <div data-id="element-3909" data-mp="usd7lkuot09e">
                    <span className="font-semibold" data-id="element-3910" data-mp="urld6dctt8kp">BP:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.bp}
                  </div>
                  <div data-id="element-3911" data-mp="3fiu92j70f9g8">
                    <span className="font-semibold" data-id="element-3912" data-mp="3fjdptwkxbrso">Urine:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.urine}
                  </div>
                  <div data-id="element-3913" data-mp="3amdkjnvwet2m">
                    <span className="font-semibold" data-id="element-3914" data-mp="11eb3nhprs6fm">Head:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.head}
                  </div>
                  <div data-id="element-3915" data-mp="wgrpeht2ntno">
                    <span className="font-semibold" data-id="element-3916" data-mp="rnyibna4s98f">Eyes:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.eyes}
                  </div>
                  <div data-id="element-3917" data-mp="nlv62wfn10m3">
                    <span className="font-semibold" data-id="element-3918" data-mp="nkk3gzxxhywk">Ears:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.ears}
                  </div>
                  <div data-id="element-3919" data-mp="dnfgixw9dzep">
                    <span className="font-semibold" data-id="element-3920" data-mp="dmvzrkichh29">Mouth:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.mouth}
                  </div>
                  <div data-id="element-3921" data-mp="4oaehhlhnnxs">
                    <span className="font-semibold" data-id="element-3922" data-mp="4p3i49p5hxbp">Neck:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.neck}
                  </div>
                  <div data-id="element-3923" data-mp="1z10dueumlpck">
                    <span className="font-semibold" data-id="element-3924" data-mp="1z2uyrt5wdhgn">Breasts:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.breasts}
                  </div>
                  <div data-id="element-3925" data-mp="3lxzwhc3enyhp">
                    <span className="font-semibold" data-id="element-3926" data-mp="3lx7xdralnimo">Ampills:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.ampills}
                  </div>
                  <div data-id="element-3927" data-mp="3uqjv4cjp632j">
                    <span className="font-semibold" data-id="element-3928" data-mp="3uqtbr9iuqxvk">Abdomen (Uterus):</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.abdomenUterus}
                  </div>
                  <div data-id="element-3929" data-mp="32z40bv7zla18">
                    <span className="font-semibold" data-id="element-3930" data-mp="32ylz0k6gw5mg">Groin:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.groin}
                  </div>
                  <div data-id="element-3931" data-mp="373bp1seg2pzg">
                    <span className="font-semibold" data-id="element-3932" data-mp="3c0lf9a9nr5oc">Perineum:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.perineum}
                  </div>
                  <div data-id="element-3933" data-mp="md48w4c81zrz">
                    <span className="font-semibold" data-id="element-3934" data-mp="r9l057epwu9a">Legs:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.legs}
                  </div>
                  <div data-id="element-3935" data-mp="2nb68ypsp8ned">
                    <span className="font-semibold" data-id="element-3936" data-mp="2jbe7sy5mxjj1">Anus:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.anus}
                  </div>
                  <div data-id="element-3937" data-mp="2xanb4x8qsj5l">
                    <span className="font-semibold" data-id="element-3938" data-mp="2x92oruwl06it">Lochia:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.lochia}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3939" data-mp="24mrhk1egz7d1">
                  Vaginal Examination
                </h4>
                <div className="space-y-2 text-sm mb-4" data-id="element-3940" data-mp="2ecb2rdpnndb2">
                  <div data-id="element-3941" data-mp="2edvy35jsn09z">
                    <span className="font-semibold" data-id="element-3942" data-mp="2ee4a6v83e1k4">Cervix per speculum:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3943" data-mp="29k9ojm8xfcie">
                      {selectedFile.postNatalSixWeeks?.mother.cervixPerSpeculum}
                    </span>
                  </div>
                  <div data-id="element-3944" data-mp="1gwnd5rtgj3kz">
                    <span className="font-semibold" data-id="element-3945" data-mp="1gv2sctwy2z0b">Family Planning:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3946" data-mp="3sixy0s4xuj6s">
                      {selectedFile.postNatalSixWeeks?.mother.familyPlanning}
                    </span>
                  </div>
                  <div data-id="element-3947" data-mp="3jl5j3p34pdjx">
                    <span className="font-semibold" data-id="element-3948" data-mp="3jhrd86v9am7q">Treatment or advise:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3949" data-mp="3nlfl6qnsvjti">
                      {selectedFile.postNatalSixWeeks?.mother.treatmentOrAdvise}
                    </span>
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3950" data-mp="k2csxmffga8y">
                  Infant Examination Form
                </h4>
                <div className="grid grid-cols-3 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-3951" data-mp="b3992g8frqte">
                  <div data-id="element-3952" data-mp="2qv6mn8mlue58">
                    <span className="font-semibold" data-id="element-3953" data-mp="2qundohexn03c">Infant:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.name}
                  </div>
                  <div data-id="element-3954" data-mp="2vrndial6v6m0">
                    <span className="font-semibold" data-id="element-3955" data-mp="3b46j0hsim0br">Weight:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.weight}
                  </div>
                  <div data-id="element-3956" data-mp="36atus8rqzvjj">
                    <span className="font-semibold" data-id="element-3957" data-mp="31dk7ozrtzvyv">Date:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.date}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-3958" data-mp="2xaf56x7r3ua8">
                  <div data-id="element-3959" data-mp="3u1psfmivxkyp">
                    <span className="font-semibold" data-id="element-3960" data-mp="3p3ek5g88niil">Head Circumference:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.headCircumference}
                  </div>
                  <div data-id="element-3961" data-mp="3l3xbyf1fg79q">
                    <span className="font-semibold" data-id="element-3962" data-mp="3g2zvc6w2flac">Length:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.length}
                  </div>
                  <div data-id="element-3963" data-mp="29nyz0034p138">
                    <span className="font-semibold" data-id="element-3964" data-mp="24nkng82bj90g">Temp:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.temp}
                  </div>
                  <div data-id="element-3965" data-mp="k73eu5uk4nc1">
                    <span className="font-semibold" data-id="element-3966" data-mp="k3fm38wxs4yd">Head:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.head}
                  </div>
                  <div data-id="element-3967" data-mp="t55k4xlbq254">
                    <span className="font-semibold" data-id="element-3968" data-mp="t4x817x0z0uz">Eyes:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.eyes}
                  </div>
                  <div data-id="element-3969" data-mp="12wt7amaukx0s">
                    <span className="font-semibold" data-id="element-3970" data-mp="12v8d4xsg3g3g">Ears:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.ears}
                  </div>
                  <div data-id="element-3971" data-mp="1cuh5jm7nwmnp">
                    <span className="font-semibold" data-id="element-3972" data-mp="1cv8z6rhp8abc">Mouth:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.mouth}
                  </div>
                  <div data-id="element-3973" data-mp="3elkg6bhjy189">
                    <span className="font-semibold" data-id="element-3974" data-mp="3em2nq49o01vh">Neck:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.neck}
                  </div>
                  <div data-id="element-3975" data-mp="3ip8qh1xkjax7">
                    <span className="font-semibold" data-id="element-3976" data-mp="psfhzjjo2hme">Chest:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.chest}
                  </div>
                  <div data-id="element-3977" data-mp="kvfb56eztau8">
                    <span className="font-semibold" data-id="element-3978" data-mp="ytdpibtbd79q">Abdomen:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.abdomen}
                  </div>
                  <div data-id="element-3979" data-mp="uq7cmlbp4hua">
                    <span className="font-semibold" data-id="element-3980" data-mp="uu4xpchu3jgt">Cord:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.cord}
                  </div>
                  <div data-id="element-3981" data-mp="13rnlgomv4bwe">
                    <span className="font-semibold" data-id="element-3982" data-mp="13s6uffujbpya">Genitalia & Rectum:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.genitaliaRectum}
                  </div>
                  <div data-id="element-3983" data-mp="1dis0tpgg9yrr">
                    <span className="font-semibold" data-id="element-3984" data-mp="1dkctf9j7f7mz">Extremities:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.extremities}
                  </div>
                  <div data-id="element-3985" data-mp="1mkj4ypvcdxji">
                    <span className="font-semibold" data-id="element-3986" data-mp="1mgvc7sxq1f5u">Back:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.back}
                  </div>
                  <div data-id="element-3987" data-mp="2b2rxn6crwvay">
                    <span className="font-semibold" data-id="element-3988" data-mp="2b31oew5n76hp">Neurologic Exam:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.neurologicExam}
                  </div>
                  <div data-id="element-3989" data-mp="214c4kxu6rbyt">
                    <span className="font-semibold" data-id="element-3990" data-mp="213u1pid0pzhx">Feeding:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.feeding}
                  </div>
                </div>
                <div className="text-sm" data-id="element-3991" data-mp="1s6jo3qcq9y9u">
                  <span className="font-semibold" data-id="element-3992" data-mp="1dg2q6gzaxqhp">Additional Notes:</span>
                  <p className="whitespace-pre-wrap mt-1" data-id="element-3993" data-mp="1ddzo2ykvk6wm">
                    {selectedFile.postNatalSixWeeks?.infant.additionalNotes}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};